import { PeriodTypes, type EmployeeField, type PeriodType, type RulePerson } from "#shared/segmentsTypes.ts";
import { ComboBox, ComboBoxContent, ComboBoxInput, ComboBoxItem } from "@/components/ui/combo-box.tsx";
import { DatePicker, DatePickerTrigger } from "@/components/ui/date-picker.tsx";
import { DateRangePicker, DateRangePickerTrigger } from "@/components/ui/date-range-picker.tsx";
import { FieldError, fieldErrorStyles } from "@/components/ui/field.tsx";
import { Input } from "@/components/ui/input.tsx";
import { NumberField, NumberInput } from "@/components/ui/number-field.tsx";
import { ProgressCircle } from "@/components/ui/progress-circle.tsx";
import { Select, SelectContent, SelectItem, SelectLabel, SelectTrigger } from "@/components/ui/select.tsx";
import { Tag, TagGroup, TagList } from "@/components/ui/tag-group.tsx";
import type { CalendarDate } from "@internationalized/date";
import { useCurrentLanguage, useI18nextInstance } from "@squide/i18next";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type { Key, Selection } from "react-aria-components";
import type { RangeValue } from "react-aria-components/DateRangePicker";
import { useTranslation } from "react-i18next";
import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { useSession } from "@packages/core-client/session/SessionContext.tsx";
import { ErrorState } from "@packages/core-client/errors/ErrorState.tsx";
import { useDebouncedValue } from "@packages/core-client/hooks/useDebouncedValue.ts";
import { segmentsI18NextInstanceKey } from "../../i18n/i18next.ts";
import { useSegmentsClient } from "../../api/segmentsQuery.ts";
import { valueArity, type RuleCondition } from "./ruleConditions.ts";
import { formatRuleDate, formatRuleNumber, parseRuleDate, parseRuleNumber, RULE_NUMBER_FRACTION_DIGITS } from "./ruleValues.ts";

const NUMBER_FORMAT_OPTIONS = { maximumFractionDigits: RULE_NUMBER_FRACTION_DIGITS, useGrouping: false };

const PERIOD_NUMBER_FORMAT_OPTIONS = { maximumFractionDigits: 0, useGrouping: false };

export interface RuleValueEditorProps {
    field: EmployeeField;
    condition: RuleCondition;
    values: string[];
    periodType?: PeriodType;
    isDisabled?: boolean;
    error?: string;
    onChange: (values: string[]) => void;
    onPeriodTypeChange?: (periodType: PeriodType) => void;
}

const PEOPLE_SEARCH_DEBOUNCE_MS = 300;

const NO_CLIENT_FILTER = () => true;

function withPositionalValue(values: string[], index: number, value: string, size: number) {
    const next = Array.from({ length: size }, (_, i) => values[i] ?? "");
    next[index] = value;

    return next;
}

interface TagComboBoxOption {
    id: string;
    name: string;
    isLoading?: boolean;
}

interface TagComboBoxProps {
    ariaLabel: string;
    tagsAriaLabel: string;
    tags: TagComboBoxOption[];
    candidates: TagComboBoxOption[];
    values: string[];
    onChange: (values: string[]) => void;
    onInputChange?: (value: string) => void;
    isDisabled?: boolean;
    isMultiple?: boolean;
    error?: string;
    placeholder: string;
    renderEmptyState?: () => React.ReactNode;
    defaultFilter?: (textValue: string, inputValue: string) => boolean;
}

function TagComboBox({ ariaLabel, tagsAriaLabel, tags, candidates, values, onChange, onInputChange, isDisabled, isMultiple = true, error, placeholder, renderEmptyState, defaultFilter }: TagComboBoxProps) {
    const [query, setQuery] = useState("");

    const handleInputChange = useCallback(
        (value: string) => {
            setQuery(value);
            onInputChange?.(value);
        },
        [onInputChange]
    );

    const handleSelectionChange = useCallback(
        (key: Key | null) => {
            if (!key) {
                return;
            }

            onChange([...values, String(key)]);
            setQuery("");
            onInputChange?.("");
        },
        [onChange, values, onInputChange]
    );

    const handleRemove = useCallback((keys: Selection) => onChange(keys === "all" ? [] : values.filter(id => !keys.has(id))), [onChange, values]);

    const handleInputKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Backspace" && !query && tags.length > 0) {
                handleRemove(new Set([tags[tags.length - 1].id]));
            }
        },
        [query, tags, handleRemove]
    );

    const errorId = useId();
    const canAddMore = isMultiple || tags.length === 0;

    return (
        <div className="w-full">
            <div
                className={[
                    "border-input focus-within:border-ring/70 focus-within:ring-ring/20 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border bg-(--control-bg,transparent) px-[calc(--spacing(3.5)-1px)] py-[calc(--spacing(2.5)-1px)] focus-within:ring-3 sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(1.5)-1px)]",
                    error && "!border-danger-subtle-fg/70 focus-within:!border-danger-subtle-fg/70 focus-within:!ring-danger-subtle-fg/20"
                ]
                    .filter(Boolean)
                    .join(" ")}
            >
                {tags.length > 0 && (
                    <TagGroup aria-label={tagsAriaLabel} onRemove={isDisabled ? undefined : handleRemove}>
                        <TagList items={tags}>
                            {tag =>
                                tag.isLoading ? (
                                    <Tag textValue={tag.name} className="max-w-64">
                                        <ProgressCircle isIndeterminate aria-label={tag.name} className="text-muted-fg size-3.5" />
                                    </Tag>
                                ) : (
                                    <Tag textValue={tag.name} className="max-w-64">
                                        <span className="min-w-0 truncate">{tag.name}</span>
                                    </Tag>
                                )
                            }
                        </TagList>
                    </TagGroup>
                )}
                {!isDisabled && canAddMore && (
                    <ComboBox aria-label={ariaLabel} inputValue={query} onInputChange={handleInputChange} selectedKey={null} onSelectionChange={handleSelectionChange} defaultFilter={defaultFilter} allowsEmptyCollection menuTrigger="focus" isInvalid={!!error} className="min-w-24 flex-1">
                        <ComboBoxInput onKeyDown={handleInputKeyDown} placeholder={tags.length === 0 ? placeholder : undefined} aria-describedby={error ? errorId : undefined} className="border-none bg-transparent !p-0 shadow-none outline-hidden focus:border-none focus:ring-0" />
                        <ComboBoxContent items={candidates} renderEmptyState={renderEmptyState}>
                            {item => (
                                <ComboBoxItem id={item.id} textValue={item.name}>
                                    <span className="truncate">{item.name}</span>
                                </ComboBoxItem>
                            )}
                        </ComboBoxContent>
                    </ComboBox>
                )}
            </div>
            {error && (
                <p id={errorId} className={fieldErrorStyles()}>
                    {error}
                </p>
            )}
        </div>
    );
}

function OptionValues({ field, values, isDisabled, isMultiple, error, onChange }: RuleValueEditorProps & { isMultiple: boolean }) {
    const i18nextInstance = useI18nextInstance(segmentsI18NextInstanceKey);
    const { t } = useTranslation("segmentRules", { i18n: i18nextInstance });
    const { queryUtils } = useSegmentsClient();
    const session = useSession();

    const {
        data: options,
        isPending,
        isError,
        refetch
    } = useQuery(
        createQueryOptions(queryUtils.listEmployeeFieldValues.queryOptions({ input: { orgId: session?.organizationId ?? "", fieldId: field.id } }), {
            throwOnError: false
        })
    );

    const handleRetry = useCallback(() => void refetch(), [refetch]);
    const handleSingleChange = useCallback((key: Key | null) => onChange(key ? [String(key)] : []), [onChange]);

    const items = useMemo(() => {
        const fetched = (options?.items ?? []).map(option => ({ id: option.value, name: option.label }));
        const missing = values.filter(value => value.trim() && !fetched.some(option => option.id === value)).map(value => ({ id: value, name: value }));

        return [...fetched, ...missing];
    }, [options, values]);

    const byId = useMemo(() => new Map(items.map(item => [item.id, item.name])), [items]);
    const tags = useMemo(() => values.map(id => ({ id, name: byId.get(id) ?? id })), [values, byId]);
    const candidates = useMemo(() => items.filter(item => !values.includes(item.id)), [items, values]);

    const renderOptionEmptyState = () => <span className="text-muted-fg block px-3 py-2 text-sm">{t("value.optionNoResults")}</span>;

    if (isError) {
        return <ErrorState isEmbedded kind="load" title={t("valuesError")} onRetry={handleRetry} retryLabel={t("retry")} />;
    }

    if (isMultiple) {
        return (
            <TagComboBox
                ariaLabel={t("columns.value")}
                tagsAriaLabel={t("value.selectedOptions")}
                tags={tags}
                candidates={candidates}
                values={values}
                onChange={onChange}
                isDisabled={isDisabled || isPending}
                error={error}
                placeholder={t("value.placeholder")}
                renderEmptyState={renderOptionEmptyState}
            />
        );
    }

    return (
        <Select aria-label={t("columns.value")} placeholder={t("value.placeholder")} selectedKey={values[0] ?? null} onSelectionChange={handleSingleChange} isDisabled={isDisabled || isPending} isInvalid={!!error}>
            <SelectTrigger />
            <SelectContent items={items}>
                {item => (
                    <SelectItem id={item.id} textValue={item.name}>
                        <SelectLabel>{item.name}</SelectLabel>
                    </SelectItem>
                )}
            </SelectContent>
            <FieldError>{error}</FieldError>
        </Select>
    );
}

function PersonValue({ values, isDisabled, isMultiple, error, onChange }: RuleValueEditorProps & { isMultiple: boolean }) {
    const i18nextInstance = useI18nextInstance(segmentsI18NextInstanceKey);
    const { t } = useTranslation("segmentRules", { i18n: i18nextInstance });
    const { queryUtils } = useSegmentsClient();
    const session = useSession();

    const [query, setQuery] = useState("");
    const debouncedQuery = useDebouncedValue(query, PEOPLE_SEARCH_DEBOUNCE_MS);
    const orgId = session?.organizationId ?? "";

    const { data: matches, isFetching: areMatchesFetching, isError: hasSearchFailed, refetch: refetchMatches } = useQuery(createQueryOptions(queryUtils.searchRulePeople.queryOptions({ input: { orgId, q: debouncedQuery.trim() || undefined } }), { throwOnError: false }));

    const {
        data: selected,
        isPending: isResolvingSelected,
        isError: hasResolveFailed,
        refetch: refetchSelected
    } = useQuery(
        createQueryOptions(queryUtils.searchRulePeople.queryOptions({ input: { orgId, ids: values } }), {
            throwOnError: false,
            enabled: values.length > 0,
            placeholderData: (previous: { items: RulePerson[] } | undefined) => previous
        })
    );

    const candidates = useMemo(() => (matches?.items ?? []).filter(person => !values.includes(person.id)).map(person => ({ id: person.id, name: person.displayName })), [matches, values]);

    const tags = useMemo(() => {
        const byId = new Map((selected?.items ?? []).concat(matches?.items ?? []).map(person => [person.id, person]));

        return values.map(id => {
            const person = byId.get(id);

            if (!person) {
                return isResolvingSelected ? { id, name: t("value.personResolving"), isLoading: true } : { id, name: id };
            }

            return { id, name: person.isDeactivated ? t("value.personDeactivatedLabel", { name: person.displayName }) : person.displayName };
        });
    }, [matches, selected, values, t, isResolvingSelected]);

    const handleRetry = useCallback(() => {
        void refetchMatches();
        void refetchSelected();
    }, [refetchMatches, refetchSelected]);

    if (hasSearchFailed || hasResolveFailed) {
        return <ErrorState isEmbedded kind="load" title={t("peopleError")} onRetry={handleRetry} retryLabel={t("retry")} />;
    }

    const isSearching = areMatchesFetching || query.trim() !== debouncedQuery.trim();

    const renderEmptyState = () =>
        isSearching ? (
            <div className="flex justify-center py-2">
                <ProgressCircle isIndeterminate aria-label={t("segmentMembers:search.loading")} className="text-muted-fg size-4" />
            </div>
        ) : (
            <span className="text-muted-fg block px-3 py-2 text-sm">{t("value.personNoResults")}</span>
        );

    return (
        <TagComboBox
            ariaLabel={t("columns.value")}
            tagsAriaLabel={t("value.selectedPeople")}
            tags={tags}
            candidates={candidates}
            values={values}
            onChange={onChange}
            onInputChange={setQuery}
            isDisabled={isDisabled}
            isMultiple={isMultiple}
            error={error}
            placeholder={t("value.personPlaceholder")}
            renderEmptyState={renderEmptyState}
            defaultFilter={NO_CLIENT_FILTER}
        />
    );
}

function NumberValue({
    values,
    isDisabled,
    error,
    onChange,
    index = 0,
    size = 1,
    minValue,
    formatOptions = NUMBER_FORMAT_OPTIONS,
    label,
    hideSteppers
}: RuleValueEditorProps & { index?: number; size?: number; minValue?: number; formatOptions?: Intl.NumberFormatOptions; label?: string; hideSteppers?: boolean }) {
    const i18nextInstance = useI18nextInstance(segmentsI18NextInstanceKey);
    const { t } = useTranslation("segmentRules", { i18n: i18nextInstance });
    const language = useCurrentLanguage();
    const fieldRef = useRef<HTMLDivElement>(null);

    const handleChange = useCallback(
        (value: number) => {
            const next = formatRuleNumber(value);
            onChange(size === 1 ? [next] : withPositionalValue(values, index, next, size));
        },
        [index, size, onChange, values]
    );

    useEffect(() => {
        const field = fieldRef.current;

        if (!field) {
            return;
        }

        const swapDecimalSeparator = (event: Event) => {
            if (language !== "fr-CA" || !(event instanceof InputEvent)) {
                return;
            }

            if (event.data === ".") {
                event.preventDefault();
                event.stopPropagation();
                document.execCommand("insertText", false, ",");

                return;
            }

            const isPasteOrDrop = event.inputType === "insertFromPaste" || event.inputType === "insertFromDrop";
            const text = event.dataTransfer?.getData("text");

            if (isPasteOrDrop && text?.includes(".")) {
                event.preventDefault();
                event.stopPropagation();
                document.execCommand("insertText", false, text.replaceAll(".", ","));
            }
        };

        field.addEventListener("beforeinput", swapDecimalSeparator, { capture: true });

        return () => field.removeEventListener("beforeinput", swapDecimalSeparator, { capture: true });
    }, [language]);

    return (
        <div ref={fieldRef} className="contents">
            <NumberField aria-label={label ?? t("columns.value")} value={parseRuleNumber(values[index] ?? "")} onChange={handleChange} formatOptions={formatOptions} minValue={minValue} isWheelDisabled isDisabled={isDisabled} isInvalid={!!error} className="min-w-0 flex-1">
                {hideSteppers ? <Input placeholder={label} /> : <NumberInput placeholder={label} />}
                <FieldError>{error}</FieldError>
            </NumberField>
        </div>
    );
}

function DateValue({ values, isDisabled, error, onChange, label }: RuleValueEditorProps & { label?: string }) {
    const i18nextInstance = useI18nextInstance(segmentsI18NextInstanceKey);
    const { t } = useTranslation("segmentRules", { i18n: i18nextInstance });

    const value = useMemo(() => parseRuleDate(values[0] ?? ""), [values]);
    const handleChange = useCallback((next: CalendarDate | null) => onChange(next ? [formatRuleDate(next)] : []), [onChange]);

    return (
        <DatePicker aria-label={label ?? t("columns.value")} value={value} onChange={handleChange} isDisabled={isDisabled} isInvalid={!!error} className="min-w-0 flex-1">
            <DatePickerTrigger />
            <FieldError>{error}</FieldError>
        </DatePicker>
    );
}

function DateRangeValue({ values, isDisabled, error, onChange }: RuleValueEditorProps) {
    const i18nextInstance = useI18nextInstance(segmentsI18NextInstanceKey);
    const { t } = useTranslation("segmentRules", { i18n: i18nextInstance });

    const start = useMemo(() => parseRuleDate(values[0] ?? ""), [values]);
    const end = useMemo(() => parseRuleDate(values[1] ?? ""), [values]);
    const value = useMemo(() => (start && end ? { start, end } : null), [start, end]);

    const handleChange = useCallback((next: RangeValue<CalendarDate> | null) => onChange(next ? [formatRuleDate(next.start), formatRuleDate(next.end)] : []), [onChange]);

    return (
        <DateRangePicker aria-label={t("columns.value")} value={value} onChange={handleChange} isDisabled={isDisabled} isInvalid={!!error} className="min-w-0 flex-1">
            <DateRangePickerTrigger />
            <FieldError>{error}</FieldError>
        </DateRangePicker>
    );
}

function PeriodTypeSelect({ periodType, isDisabled, error, onPeriodTypeChange }: RuleValueEditorProps) {
    const i18nextInstance = useI18nextInstance(segmentsI18NextInstanceKey);
    const { t } = useTranslation("segmentRules", { i18n: i18nextInstance });

    const handlePeriodTypeChange = useCallback(
        (key: Key | null) => {
            const next = PeriodTypes.find(candidate => candidate === key);

            if (next) {
                onPeriodTypeChange?.(next);
            }
        },
        [onPeriodTypeChange]
    );

    return (
        <Select aria-label={t("value.periodType")} placeholder={t("value.periodTypePlaceholder")} selectedKey={periodType ?? null} onSelectionChange={handlePeriodTypeChange} isDisabled={isDisabled} isInvalid={!!error} className="w-28 shrink-0">
            <SelectTrigger />
            <SelectContent items={PeriodTypes.map(value => ({ id: value }))}>
                {item => (
                    <SelectItem id={item.id} textValue={t(`value.periodTypes.${item.id}`)}>
                        <SelectLabel>{t(`value.periodTypes.${item.id}`)}</SelectLabel>
                    </SelectItem>
                )}
            </SelectContent>
            <FieldError>{error}</FieldError>
        </Select>
    );
}

function PeriodValue(props: RuleValueEditorProps) {
    const i18nextInstance = useI18nextInstance(segmentsI18NextInstanceKey);
    const { t } = useTranslation("segmentRules", { i18n: i18nextInstance });

    return (
        <div className="flex flex-wrap items-start gap-2">
            <NumberValue {...props} minValue={0} formatOptions={PERIOD_NUMBER_FORMAT_OPTIONS} label={t("value.periodCount")} hideSteppers />
            <PeriodTypeSelect {...props} />
        </div>
    );
}

function PeriodRangeValue(props: RuleValueEditorProps) {
    const i18nextInstance = useI18nextInstance(segmentsI18NextInstanceKey);
    const { t } = useTranslation("segmentRules", { i18n: i18nextInstance });
    const isStartEmpty = !props.values[0]?.trim();

    return (
        <div className="flex flex-wrap items-start gap-2">
            <NumberValue {...props} index={0} size={2} minValue={0} formatOptions={PERIOD_NUMBER_FORMAT_OPTIONS} label={t("value.rangeStart")} error={isStartEmpty ? props.error : undefined} hideSteppers />
            <NumberValue {...props} index={1} size={2} minValue={0} formatOptions={PERIOD_NUMBER_FORMAT_OPTIONS} label={t("value.rangeEnd")} error={isStartEmpty ? undefined : props.error} hideSteppers />
            <PeriodTypeSelect {...props} />
        </div>
    );
}

export function RuleValueEditor(props: RuleValueEditorProps) {
    const i18nextInstance = useI18nextInstance(segmentsI18NextInstanceKey);
    const { t } = useTranslation("segmentRules", { i18n: i18nextInstance });

    const arity = valueArity(props.condition);

    if (arity === "range") {
        if (props.field.type === "date") {
            return <DateRangeValue {...props} />;
        }

        const isStartEmpty = !props.values[0]?.trim();

        return (
            <div className="flex items-start gap-2">
                <NumberValue {...props} index={0} size={2} label={t("value.rangeStart")} error={isStartEmpty ? props.error : undefined} />
                <NumberValue {...props} index={1} size={2} label={t("value.rangeEnd")} error={isStartEmpty ? undefined : props.error} />
            </div>
        );
    }

    if (arity === "period") {
        return <PeriodValue {...props} />;
    }

    if (arity === "periodRange") {
        return <PeriodRangeValue {...props} />;
    }

    if (props.field.type === "person" || props.field.type === "personReference") {
        return <PersonValue {...props} isMultiple={arity === "multiple"} />;
    }

    if (props.field.type === "option") {
        return <OptionValues {...props} isMultiple={arity === "multiple"} />;
    }

    if (props.field.type === "date") {
        return <DateValue {...props} />;
    }

    return <NumberValue {...props} />;
}
